import { DocumentContext } from '../types'
import { computed, reactive, readonly, shallowRef } from 'vue'
import {
  DocumentAcceptance,
  DocumentAcceptanceConfirmation,
  DocumentRouteSignature,
} from '@/utils/types/document'
import {
  documentAcceptanceConfirmationCreate,
  documentAcceptanceConfirmationList,
  documentAcceptanceDelete,
  documentAcceptanceList,
  documentAcceptanceStatus,
  documentSignatureCreate,
} from '@/requests/document'
import { openDialog } from '@/dialog'
import { useStore } from 'vuex'
import { handleAsync, setSnackbar } from 'best-modules/plugins'
import { ISODate } from 'best-modules/utils/customDate'
import { DefaultItem } from 'best-modules/plugins/directory/types'
import { SignedDocument } from '@/dialog/components/sign-document/types'

const ACCEPTANCE_WITH_SIGNATURE_TYPE_ID = 2
const ACCEPTANCE_SIGNATURE_CONTEXT_TYPE_ID = 2

type AcceptanceOptions = {
  addDocumentSignatures?: (_signatures: DocumentRouteSignature[]) => void
}

export function useAcceptance(
  { documentId, documentSource }: DocumentContext,
  options: AcceptanceOptions = {}
) {
  const store = useStore()
  const userId = computed(() => store.state.user.userData?.id)
  const acceptanceStatus = reactive<{
    isInAcceptanceList: boolean
    acceptanceType: DefaultItem | null
    isConfirmed: boolean
    confirmedAt: ISODate | null
  }>({
    isInAcceptanceList: false,
    acceptanceType: null,
    isConfirmed: false,
    confirmedAt: null,
  })
  const acceptanceList = shallowRef<DocumentAcceptance[]>([])
  const acceptanceConfirmationList = shallowRef<
    DocumentAcceptanceConfirmation[]
  >([])

  const getAcceptanceStatus = () => {
    return documentAcceptanceStatus(documentId.value).then(res => {
      Object.assign(acceptanceStatus, res)
    })
  }

  const getAcceptanceList = () => {
    return documentAcceptanceList(documentId.value).then(res => {
      const list = []

      for (const acceptance of res) {
        if (
          acceptance.acceptorTypeId === 1 &&
          !acceptance.acceptor.confirmed_at
        ) {
          list.push(acceptance)
        }
        if (acceptance.acceptorTypeId === 2) {
          acceptance.acceptor.activeUsers =
            acceptance.acceptor.activeUsers.filter(u => !u.confirmed_at)

          if (acceptance.acceptor.activeUsers.length) {
            list.push(acceptance)
          }
        }
      }

      acceptanceList.value = list
    })
  }
  const getAcceptanceConfirmationList = () => {
    return documentAcceptanceConfirmationList(documentId.value).then(res => {
      acceptanceConfirmationList.value = res
    })
  }

  const refreshAcceptance = () => {
    setSnackbar({ text: 'Ви успішно ознайомились з документом' })
    getAcceptanceList()
    getAcceptanceConfirmationList()
    getAcceptanceStatus()
  }

  const acceptanceCreate = (acceptanceTypeId: number) => {
    openDialog({
      name: 'DocumentAcceptance',
      dialogData: {
        acceptanceTypeId,
        acceptanceList: readonly(acceptanceList.value),
        documentId: documentId.value,
      },
      params: {
        cb: getAcceptanceList,
      },
    })
  }

  const showAcceptanceParticipants = () => {
    openDialog({
      name: 'DocumentAcceptanceParticipants',
      dialogData: {
        acceptanceList: acceptanceList.value,
        acceptanceConfirmationList: acceptanceConfirmationList.value,
      },
      params: {
        hideActionButtons: true,
      },
    })
  }

  const acceptanceDelete = (acceptanceId: number) => {
    return handleAsync(`acceptanceDelete_${acceptanceId}`, () => {
      return documentAcceptanceDelete(acceptanceId).then(() => {
        acceptanceList.value = acceptanceList.value.filter(
          a => a.id !== acceptanceId
        )
      })
    })
  }

  const createConfirmation = () => {
    return documentAcceptanceConfirmationCreate({
      docTypeDocumentId: documentId.value,
      userId: userId.value,
      acceptanceTypeId: acceptanceStatus.acceptanceType?.id || 1,
    })
  }

  const createAcceptanceSignatures = (
    confirmation: DocumentAcceptanceConfirmation,
    signatures: SignedDocument[]
  ) => {
    if (!signatures.length) {
      return Promise.reject(new Error('Документ не було підписано'))
    }

    return documentSignatureCreate(
      signatures.map(signature => {
        return {
          docTypeDocumentId: documentId.value,
          documentSigned: signature.documentSigned,
          signingDate: signature.signingDate,
          fullCertificate: signature.fullCertificate,
          signatureContextTypeId: ACCEPTANCE_SIGNATURE_CONTEXT_TYPE_ID,
          signatureContextId: confirmation.id,
        }
      })
    ).then(createdSignatures => {
      options.addDocumentSignatures?.(createdSignatures)
      return createdSignatures
    })
  }

  const handleAcceptanceError = (e: any) => {
    const msg =
      e?.response?.data?.errors?.message?.[0] ||
      e?.response?.data?.message ||
      e?.message ||
      'Помилка виконання дії'
    setSnackbar({ text: msg, color: 'error' })
  }

  const signAndCreateConfirmation = () => {
    if (!documentSource.document?.length) {
      return setSnackbar({
        text: 'Документ не містить файлів для підписання',
        color: 'warning',
      })
    }

    openDialog({
      name: 'SignDocument',
      dialogData: {
        documents: documentSource.document.map(d => {
          return {
            document: d,
            uploadFilesParams: {
              mainFolder: 'document',
              nameFolder: 'signature',
              prefixFolder: documentSource.uuid,
            },
          }
        }),
      },
      params: {
        hideActionButtons: true,
        cb: async signatures => {
          if (!Array.isArray(signatures)) return

          try {
            const confirmation = await createConfirmation()
            await createAcceptanceSignatures(confirmation, signatures)
            refreshAcceptance()
          } catch (e) {
            handleAcceptanceError(e)
            throw e
          }
        },
      },
    })
  }

  const acceptanceConfirmationCreate = () => {
    if (
      acceptanceStatus.acceptanceType?.id === ACCEPTANCE_WITH_SIGNATURE_TYPE_ID
    ) {
      return signAndCreateConfirmation()
    }

    return createConfirmation()
      .then(refreshAcceptance)
      .catch(handleAcceptanceError)
  }

  const currentAcceptance = computed<{
    acceptanceExist: boolean
    confirmed_at: ISODate
  }>(() => {
    const result = {
      acceptanceExist: false,
      confirmed_at: null as any,
    }
    for (const acceptance of acceptanceList.value) {
      if (
        acceptance.acceptorTypeId === 1 &&
        acceptance.acceptorId === userId.value
      ) {
        result.acceptanceExist = true
        result.confirmed_at = acceptance.acceptor.confirmed_at
      }

      if (acceptance.acceptorTypeId === 2) {
        for (const user of acceptance.acceptor.activeUsers) {
          if (user.id === userId.value) {
            result.acceptanceExist = true
            result.confirmed_at = user.confirmed_at

            break
          }
        }
      }

      if (result.acceptanceExist) {
        break
      }
    }

    return result
  })

  return {
    acceptanceStatus,
    acceptanceList,
    acceptanceConfirmationList,
    getAcceptanceStatus,
    getAcceptanceList,
    getAcceptanceConfirmationList,
    acceptanceCreate,
    showAcceptanceParticipants,
    acceptanceDelete,
    acceptanceConfirmationCreate,
    currentAcceptance,
  }
}
